import { useLocation, useSearchParams, useNavigate } from 'react-router-dom';
import Hero from '../../components/Hero';

import CitiesSection from '../../components/CitiesSection';
import SelectedCitySection from '../../components/SelectedCitySection';
import { useEffect, useMemo, useRef } from 'react';
import { useGetBusinessData } from '../../../Utility';
import {
  buildHomeCityPath,
  getListingCityFromPathname,
} from '../../../utils/listingPaths';

function HomePage() {
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const navigate = useNavigate();
  const selectedCity =
    getListingCityFromPathname(location.pathname) ||
    searchParams.get('city') ||
    '';

  useEffect(() => {
    if (location.pathname !== '/') return;
    const c = searchParams.get('city');
    if (!c) return;
    navigate(buildHomeCityPath(c), { replace: true });
  }, [location.pathname, searchParams, navigate]);
  const businessData = useGetBusinessData();
  const prevCityRef = useRef(selectedCity);
  const scrollTriggeredRef = useRef(false);
  const topCities = useMemo(() => {
    const list = businessData?.businessesList ?? [];
    const counts = new Map();
    list.forEach((business) => {
      const city = String(business?.city ?? '').trim();
      const topRank = business?.top_rank;
      const hasTopRank =
        topRank !== null &&
        topRank !== undefined &&
        topRank !== '' &&
        !Number.isNaN(Number(topRank));
      if (!city || !hasTopRank) return;
      counts.set(city, (counts.get(city) || 0) + 1);
    });
    return Array.from(counts.entries())
      .sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]))
      .map(([city]) => city);
  }, [businessData?.businessesList]);

  // Smooth scroll to explore section ONLY when city is selected from Hero section (user near top of page)
  useEffect(() => {
    const isHomePath =
      location.pathname === '/' || location.pathname.startsWith('/city/');
    const cityChanged = prevCityRef.current !== selectedCity;
    const hasCity = !!selectedCity;

    // Only scroll if:
    // 1. We're on homepage
    // 2. City changed
    // 3. There's a city selected
    // 4. User is near the top of the page (within hero section) - meaning they used Hero dropdown, not Header
    if (isHomePath && cityChanged && hasCity) {
      // Check if user is in the hero section area (near top of page)
      const currentScrollY = window.scrollY || window.pageYOffset;
      const heroSection = document.querySelector('.hero');
      const heroHeight = heroSection ? heroSection.offsetHeight : 800; // Fallback to 800px if hero not found
      
      // Only scroll if user is within the hero section (near top)
      // This means they're using the Hero dropdown, not the Header dropdown
      const isInHeroArea = currentScrollY < heroHeight * 1.5; // Within 1.5x hero height from top
      
      if (isInHeroArea) {
        // Reset scroll trigger flag when city changes
        scrollTriggeredRef.current = false;
        
        // Wait for SelectedCitySection to render, then scroll
        const scrollTimer = setTimeout(() => {
          const exploreSection = document.getElementById('explore-section');
          const header = document.querySelector('.header');
          
          if (exploreSection && !scrollTriggeredRef.current) {
            // Calculate header height dynamically
            const headerHeight = header ? header.offsetHeight : 0;
            
            // Get the position of the explore section
            const exploreSectionTop = exploreSection.getBoundingClientRect().top + window.pageYOffset;
            
            // Calculate scroll position with header offset
            const scrollPosition = exploreSectionTop - headerHeight - 20; // 20px extra padding
            
            // Smooth scroll to the calculated position
            window.scrollTo({
              top: scrollPosition,
              behavior: 'smooth'
            });
            
            scrollTriggeredRef.current = true;
          }
        }, 200); // Slightly longer delay to ensure rendering

        return () => clearTimeout(scrollTimer);
      }
    }

    prevCityRef.current = selectedCity;
  }, [selectedCity, location.pathname]);

  return (
    <>
      <Hero />
      {selectedCity ? (
        <SelectedCitySection />
      ) : (
        <>
          {topCities.length > 0 && (
            <CitiesSection
              cityList={topCities}
              title="Top Cities"
              subtitle="Explore cities with top-ranked venues and vendors"
              sectionId="top-cities-section"
            />
          )}
          <CitiesSection />
        </>
      )}
      {/* <Features /> */}
    </>
  );
}

export default HomePage;
